import type { GridColumn } from '../views/DataGrid';
import { orderedFields, type ResourceMeta } from '../meta/types';
import type { ColumnDef } from './types';
import { getPath } from '../meta/path';

// ScreenDef.columns + ResourceMeta → GridColumn[]. Strings reference meta fields; objects
// override label/width/render; fields not mentioned stay available (hidden) in the chooser
// so a saved view can bring them back.

export function columnsFromMeta(meta: ResourceMeta, defs?: ColumnDef[]): GridColumn[] {
  const fields = orderedFields(meta);
  const byName = new Map(fields.map((f) => [f.name, f]));
  const out: GridColumn[] = [];
  const used = new Set<string>();

  if (defs && defs.length) {
    for (const d of defs) {
      const def = typeof d === 'string' ? { field: d } : d;
      const f = byName.get(def.field);
      used.add(def.field);
      out.push({
        id: def.field,
        header: def.label ?? f?.label ?? def.field,
        field: f,
        width: def.width ?? f?.width,
        align: def.align ?? f?.align,
        sortable: def.sortable ?? f?.sortable ?? false,
        sortKey: def.sortKey,
        cell: def.render ? (row) => def.render!(row) : undefined,
        hidden: def.hidden,
        editable: def.editable,
      });
    }
    // Remaining list-worthy fields, hidden by default.
    for (const f of fields) {
      if (used.has(f.name) || !f.inList) continue;
      out.push({ id: f.name, header: f.label, field: f, width: f.width, align: f.align, sortable: f.sortable, hidden: true, ...(f.name.includes('.') ? { accessor: (row: Record<string, unknown>) => getPath(row, f.name) } : {}) });
    }
    return out;
  }

  for (const f of fields) {
    if (!f.inList && !f.inDetail) continue;
    out.push({ id: f.name, header: f.label, field: f, width: f.width, align: f.align, sortable: f.sortable, hidden: !f.inList, ...(f.name.includes('.') ? { accessor: (row: Record<string, unknown>) => getPath(row, f.name) } : {}) });
  }
  return out;
}
